import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Table, Column, HeaderCell, Cell } from 'rsuite-table'
import 'rsuite-table/dist/css/rsuite-table.css'
import moment from 'moment'
import Glyph from 'component/utils/Glyph'
import DownloadSVG from 'component/svgComponents/download'
import './styles.css'

const SHADOW_HEADER_CLASS = 'shadow-table-header'
const SCROLL_BOTTOM_SHADOW_CLASS = 'scrollbar-bottom-shadow'
const HIDE_SHADOW_CLASS = 'hide-shadow'
const HEADER_HEIGHT = 45
const ROW_HEIGHT = 40
const HEADER_AND_SCROLL_SIZE = 30
const PAGE_CHROME = 390
const DEFAULT_SCREEN_HEIGHT = Math.max(window.innerHeight - PAGE_CHROME, 200)

const formatDateTime = (value) => {
  if (!value) return '-'
  return moment(value).format('DD/MM/YYYY')
}

const formatDecimal = (value) => {
  if (value === null || value === undefined || value === '') return '0,00'
  return Number(value).toFixed(2).replace('.', ',')
}

const SatisfactionSurveyTable = ({ items, translate, onViewDetails, onEdit, onDelete, onDownload, children }) => {
  const wrapperRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(400)

  useEffect(() => {
    const dynamicSize = items.length <= 2 ? 120 : items.length * ROW_HEIGHT
    if (dynamicSize > DEFAULT_SCREEN_HEIGHT) {
      setTableHeight(DEFAULT_SCREEN_HEIGHT + HEADER_AND_SCROLL_SIZE)
    } else {
      setTableHeight(dynamicSize + HEADER_HEIGHT + HEADER_AND_SCROLL_SIZE)
    }
    if (window.innerHeight < 745) {
      setTableHeight(300)
    }
  }, [items.length])

  const getBodyWrapper = () =>
    wrapperRef.current?.querySelector('.rs-table-body-row-wrapper')

  const getTableHeader = () =>
    wrapperRef.current?.querySelector('.rs-table-row-header')

  const updateFooterShadow = (scrollY = null) => {
    const wrapperElement = wrapperRef.current
    if (!wrapperElement) return

    const footerShadow = wrapperElement.querySelector('.box-footer-shadow')
    const verticalScrollbar = wrapperElement.querySelector('.rs-table-scrollbar-vertical')
    const horizontalScrollbar = wrapperElement.querySelector('.rs-table-scrollbar-horizontal')

    if (!footerShadow) return

    footerShadow.style.top = horizontalScrollbar ? '-4px' : '5px'

    if (!verticalScrollbar) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
      return
    }

    const bodyWrapper = getBodyWrapper()
    if (!bodyWrapper) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
      return
    }

    const maxScrollTop = bodyWrapper.scrollHeight - bodyWrapper.clientHeight
    const currentScrollTop = typeof scrollY === 'number' ? scrollY : bodyWrapper.scrollTop
    const isAtBottom = currentScrollTop >= maxScrollTop - 20

    if (isAtBottom) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
    } else {
      footerShadow.classList.add(SCROLL_BOTTOM_SHADOW_CLASS)
      footerShadow.classList.remove(HIDE_SHADOW_CLASS)
    }
  }

  const handleScroll = (scrollX, scrollY) => {
    const tableHeader = getTableHeader()
    if (tableHeader && scrollY >= 30 && !tableHeader.classList.contains(SHADOW_HEADER_CLASS)) {
      tableHeader.classList.add(SHADOW_HEADER_CLASS)
    } else if (tableHeader && scrollY < 30 && tableHeader.classList.contains(SHADOW_HEADER_CLASS)) {
      tableHeader.classList.remove(SHADOW_HEADER_CLASS)
    }
    updateFooterShadow(scrollY)
  }

  useEffect(() => {
    const refreshLayout = () => {
      window.dispatchEvent(new Event('resize'))
      updateFooterShadow()
    }
    const immediateTimer = window.setTimeout(refreshLayout, 0)
    const delayedTimer = window.setTimeout(refreshLayout, 150)
    return () => {
      window.clearTimeout(immediateTimer)
      window.clearTimeout(delayedTimer)
    }
  }, [items, tableHeight])

  useEffect(() => {
    const bodyWrapper = getBodyWrapper()
    if (!bodyWrapper) return undefined
    const syncFooterShadow = () => updateFooterShadow(bodyWrapper.scrollTop)
    syncFooterShadow()
    bodyWrapper.addEventListener('scroll', syncFooterShadow)
    return () => bodyWrapper.removeEventListener('scroll', syncFooterShadow)
  }, [items, tableHeight])

  return (
    <div ref={wrapperRef} className='wrapper-table-relatory'>
      <Table
        data={items}
        height={tableHeight}
        headerHeight={HEADER_HEIGHT}
        minHeight={150}
        rowHeight={ROW_HEIGHT}
        cellBordered={false}
        rowClassName='relatory-table-row'
        onScroll={handleScroll}
      >
        <Column flexGrow={1} minWidth={80} verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory first-col-report'>
            {translate('satisfactionSurvey.table.description')}
          </HeaderCell>
          <Cell>
            {(rowData) => rowData.description || '-'}
          </Cell>
        </Column>

        <Column width={130} verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.begin')}
          </HeaderCell>
          <Cell>
            {(rowData) => formatDateTime(rowData.dtBegin)}
          </Cell>
        </Column>

        <Column width={130} verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.end')}
          </HeaderCell>
          <Cell>
            {(rowData) => formatDateTime(rowData.dtEnd)}
          </Cell>
        </Column>

        <Column width={110} align='center' verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.quantity')}
          </HeaderCell>
          <Cell>
            {(rowData) => rowData.quantity ?? 0}
          </Cell>
        </Column>

        <Column width={110} align='center' verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.average')}
          </HeaderCell>
          <Cell>
            {(rowData) => formatDecimal(rowData.average)}
          </Cell>
        </Column>

        <Column width={100} align='center' verticalAlign='middle'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.nps')}
          </HeaderCell>
          <Cell>
            {(rowData) => formatDecimal(rowData.nps)}
          </Cell>
        </Column>

        <Column width={180} align='center' verticalAlign='middle' fixed='right'>
          <HeaderCell className='custom-header-relatory'>
            {translate('satisfactionSurvey.table.actions')}
          </HeaderCell>
          <Cell>
            {(rowData) => {
              const isPast = moment().isAfter(moment(rowData.dtEnd), 'day')
              const isFuture = moment().isBefore(moment(rowData.dtBegin), 'day')
              const hasNoResponses = !rowData.quantity
              return (
                <div className='satisfaction-survey-action-buttons'>
                  <button
                    type='button'
                    className='satisfaction-survey-action-button'
                    onClick={() => onViewDetails(rowData)}
                    title={translate('satisfactionSurvey.details.view')}
                  >
                    <Glyph
                      type='eye'
                      color='primary'
                      customClass='satisfaction-survey-action-glyph'
                    />
                  </button>
                  <button
                    type='button'
                    className={`satisfaction-survey-action-button${isPast ? ' satisfaction-survey-action-button-disabled' : ''}`}
                    onClick={() => !isPast && onEdit(rowData)}
                    disabled={isPast}
                    title={translate('satisfactionSurvey.details.edit')}
                  >
                    <Glyph
                      type='pencil'
                      color='primary'
                      customClass='satisfaction-survey-action-glyph'
                    />
                  </button>
                  <button
                    type='button'
                    className={`satisfaction-survey-action-button satisfaction-survey-action-button-danger${!isFuture ? ' satisfaction-survey-action-button-disabled' : ''}`}
                    onClick={() => isFuture && onDelete(rowData)}
                    disabled={!isFuture}
                    title={translate('satisfactionSurvey.details.delete')}
                  >
                    <Glyph
                      type='trash-can'
                      color='danger'
                      customClass='satisfaction-survey-action-glyph'
                    />
                  </button>
                  <button
                    type='button'
                    className={`satisfaction-survey-action-button${hasNoResponses ? ' satisfaction-survey-action-button-disabled' : ''}`}
                    onClick={() => !hasNoResponses && onDownload(rowData)}
                    disabled={hasNoResponses}
                    title={translate('satisfactionSurvey.download')}
                  >
                    <DownloadSVG
                      className='satisfaction-survey-download-icon'
                      fill={hasNoResponses ? '#bdbdbd' : '#41a017'}
                    />
                  </button>
                </div>
              )
            }}
          </Cell>
        </Column>
      </Table>

      <div className='box-footer-shadow hide-shadow'>&nbsp;</div>

      {children}
    </div>
  )
}

SatisfactionSurveyTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  translate: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  children: PropTypes.node,
}

export default SatisfactionSurveyTable
